// Teacher and room dropdowns of a group plan (SPEC §7.3): teachers not qualified for the course are marked,
// archived teachers and rooms are not offered.

import type { Classroom, Teacher } from "../../api/types";
import { currentLocale, t } from "../../i18n/i18n";
import { sortByName } from "../../text";
import { createSelectField, type Field, type SelectOption } from "../../ui/forms";
import { classroomWithCenter, type ReferenceData } from "../referenceData";

/** The teacher dropdown for the course; refill it when the course changes. */
export function createTeacherField(reference: ReferenceData, courseId: string, teacherId: string):
  Field<HTMLSelectElement> {
  const field: Field<HTMLSelectElement> = createSelectField(t("groups.mainTeacher"), [], teacherId);
  fillTeacherOptions(field.input, reference, courseId, teacherId);
  return field;
}

/** Replaces the options, marking teachers who can't teach the course. */
export function fillTeacherOptions(select: HTMLSelectElement, reference: ReferenceData, courseId: string,
  selectedId: string): void {
  const active: Teacher[] = reference.teachers.filter((teacher: Teacher): boolean => teacher.archived === false);
  const options: SelectOption[] = sortByName(active, currentLocale()).map((teacher: Teacher): SelectOption => ({
    value: teacher.id,
    label: describeTeacher(teacher, courseId),
  }));
  select.replaceChildren();
  for (const option of [{ value: "", label: t("app.none") }, ...options]) {
    const element: HTMLOptionElement = document.createElement("option");
    element.value = option.value;
    element.textContent = option.label;
    select.append(element);
  }

  select.value = selectedId;
}

/** The room dropdown: non-archived classrooms with their center. */
export function createRoomField(reference: ReferenceData, classroomId: string): Field<HTMLSelectElement> {
  const options: SelectOption[] = reference.classrooms
    .filter((classroom: Classroom): boolean => classroom.archived === false)
    .map((classroom: Classroom): SelectOption => ({
      value: classroom.id,
      label: classroomWithCenter(reference, classroom.id),
    }));
  return createSelectField(t("groups.room"), [{ value: "", label: t("app.none") }, ...options], classroomId);
}

function describeTeacher(teacher: Teacher, courseId: string): string {
  const name: string = `${teacher.lastName} ${teacher.firstName}`;
  const isQualified: boolean = courseId === "" || teacher.courseIds.includes(courseId);
  return isQualified ? name : `${name} (${t("groups.notQualified")})`;
}
